import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  TextField,
  Paper,
  Typography,
  Stack,
  IconButton,
  Card,
  CardContent,
  Divider,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ArrowBackOutlined as ArrowBackOutlinedIcon,
  YouTube as YouTubeIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Article as ArticleIcon,
} from '@mui/icons-material';
import { MainContentArea, MainContentAreaChildGrid } from './styled';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { testVideo } from './testVideo';
import MarkdownRenderer from '../../components/MarkdownRenderer';

const EditVideoDetails = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [openRecipeDialog, setOpenRecipeDialog] = useState(false);

  // Replace these with your actual selectors
  // const video = useSelector(state => state.videos.currentVideo);
  const video = testVideo;
  // const userRecipes = useSelector(state => state.recipes.userRecipes);

  // Local state for editing
  const [editForm, setEditForm] = useState({
    title: video?.title || '',
    description: video?.description || '',
    youtubeUrl: video?.youtubeUrl || '',
    transcription: video?.transcription || '',
    relatedRecipes: video?.relatedRecipes || [],
  });

  const handleSave = () => {
    // Handle save logic here
    setIsEditing(false);
  };

  // Display Mode Component
  const DisplayMode = () => (
    <Stack spacing={3}>
      {/* Video Embed */}
      <Card elevation={3}>
        <Box sx={{ position: 'relative', paddingTop: '56.25%' }}>
          <iframe
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
            }}
            src={video?.embedUrl}
            title={video?.title}
            frameBorder="0"
            allowFullScreen
          />
        </Box>
      </Card>

      {/* Video Details */}
      <Card elevation={3}>
        <CardContent>
          <Typography variant="h4" gutterBottom>
            {video?.title}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            Uploaded by {video?.author}
          </Typography>
          <Divider sx={{ my: 2 }} />
          <MarkdownRenderer content={video?.description}/>
        </CardContent>
      </Card>

      {/* Related Recipes */}
      {video?.relatedRecipes?.length > 0 && (
        <Card elevation={3}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Related Recipes
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
              {video.relatedRecipes.map((recipe) => (
                <Chip
                  key={recipe.id}
                  label={recipe.title}
                  onClick={() => navigate(`/recipe/${recipe.id}`)}
                />
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Transcription */}
      {video?.transcription && (
        <Card elevation={3}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Transcription
            </Typography>
            <MarkdownRenderer content={video.transcription} />
          </CardContent>
        </Card>
      )}
    </Stack>
  );

  // Edit Mode Component
  const EditMode = () => (
    <Stack spacing={3}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Stack spacing={2}>
          <TextField
            label="Video Title"
            variant="outlined"
            fullWidth
            required
            value={editForm.title}
            onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
          />

          <TextField
            label="YouTube URL"
            variant="outlined"
            fullWidth
            required
            value={editForm.youtubeUrl}
            onChange={(e) => setEditForm({ ...editForm, youtubeUrl: e.target.value })}
          />

          <TextField
            label="Video Description"
            variant="outlined"
            fullWidth
            required
            multiline
            rows={4}
            value={editForm.description}
            onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
          />

          <TextField
            label="Video Transcription"
            variant="outlined"
            fullWidth
            multiline
            rows={6}
            value={editForm.transcription}
            onChange={(e) => setEditForm({ ...editForm, transcription: e.target.value })}
          />

          {/* Related Recipes Section */}
          <Typography variant="h6">Related Recipes</Typography>
          <Button
            variant="outlined"
            startIcon={<SearchIcon />}
            onClick={() => setOpenRecipeDialog(true)}
          >
            Search Your Recipes
          </Button>

          {editForm.relatedRecipes.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
              {editForm.relatedRecipes.map((recipe) => (
                <Chip
                  key={recipe.id}
                  label={recipe.title}
                  onDelete={() => {
                    setEditForm({
                      ...editForm,
                      relatedRecipes: editForm.relatedRecipes.filter(r => r.id !== recipe.id)
                    });
                  }}
                />
              ))}
            </Stack>
          )}
        </Stack>
      </Paper>
    </Stack>
  );

  return (
    <MainContentArea elevation={0}>
      <MainContentAreaChildGrid container>
        {/* Header */}
        <Container 
          sx={{ 
            backgroundColor: theme.palette.primary.main,
            height: '3rem',
            width: '100%',
            padding: '1rem 1rem',
            borderBottomLeftRadius: '2rem',
            borderBottomRightRadius: '2rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Button
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-around',
              width: '6rem'
            }}
            onClick={() => navigate(-1)}
            color='secondary'
          >
            <ArrowBackOutlinedIcon />
            <Typography variant='h7' color='secondary'>
              Back
            </Typography>
          </Button>
          
          <Button
            color='secondary'
            startIcon={isEditing ? <SaveIcon /> : <EditIcon />}
            onClick={() => isEditing ? handleSave() : setIsEditing(true)}
          >
            {isEditing ? 'Save' : 'Edit'}
          </Button>
        </Container>

        {/* Main Content */}
        <Container maxWidth="md" sx={{ py: 4 }}>
          {isEditing ? <EditMode /> : <DisplayMode />}
          
          {/* Action Buttons */}
          {isEditing && (
            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                color="error"
                fullWidth
                startIcon={<CancelIcon />}
                onClick={() => setIsEditing(false)}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                startIcon={<SaveIcon />}
                onClick={handleSave}
              >
                Save Changes
              </Button>
            </Box>
          )}
        </Container>

        {/* Recipe Selection Dialog */}
        <Dialog 
          open={openRecipeDialog} 
          onClose={() => setOpenRecipeDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Select Recipes
            <IconButton
              onClick={() => setOpenRecipeDialog(false)}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Search recipes"
              type="search"
              fullWidth
              variant="outlined"
              sx={{ mb: 2 }}
            />
            <List sx={{ pt: 0 }}>
              {/* {userRecipes.map((recipe) => (
                <ListItem
                  button
                  key={recipe.id}
                  onClick={() => {
                    if (!editForm.relatedRecipes.find(r => r.id === recipe.id)) {
                      setEditForm({
                        ...editForm,
                        relatedRecipes: [...editForm.relatedRecipes, recipe]
                      });
                    }
                    setOpenRecipeDialog(false);
                  }}
                >
                  <ListItemAvatar>
                    <Avatar src={recipe.image} alt={recipe.title}>
                      <ArticleIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText primary={recipe.title} />
                </ListItem>
              ))} */}
            </List>
          </DialogContent>
        </Dialog>
      </MainContentAreaChildGrid>
    </MainContentArea>
  );
};

export default EditVideoDetails;